import { api, AxiosResponse } from 'api/axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuth } from 'utils/AuthProvider';
import { AppointmentType } from './useAppointments';

export const LinkJapanVendor = 'linkjapan';
export const MedicalcarePid = 'medicalcare';
export const MedicineDeliveryPid = 'medicine_delivery';

export type InsuranceImage = {
  extraInfo: {
    height: number;
    width: number;
  };
  imageURL: string;
  thumbURL: string;
  title: string;
  type: string;
};

export type PatientInfo = {
  address: string;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  insuranceImageBack?: InsuranceImage | null;
  insuranceImageFront?: InsuranceImage | null;
  insuranceNumber: string;
  name: string;
  phone: string;
  sex: string;
  zipPrefix: string;
  zipSuffix: string;
};

export type Family = {
  id: string;
  name: string;
  buildingId: string | null;
  userId: string;
  app: string | null;
  createdAt: string;
  updatedAt: string;
  order: number | null;
  detailSetting: string;
};

export interface DeviceData {
  deviceId: string;
  deviceName: string;
  did: string;
  familyDevicesLocked: boolean;
  familyId: string;
  familyName: string;
  ownerUserEmail: string;
  ownerUserId: string;
  pid: string;
  vendor: string;
  name?: string;
  extraInfo?: any;
  roomId?: string | null;
}

export async function searchDevices(
  userId: string,
  vendor: string,
  pid: string,
): Promise<AxiosResponse<DeviceData[]>> {
  const response = await api.get(
    `/device/device-search?vendor=${vendor}&pid=${pid}&userId=${userId}`,
  );
  return response.data;
}

export async function createDevice(data: Partial<DeviceData>) {
  const response = await api.post(`/device`, data);
  return response.data;
}

export async function updateDevice(
  deviceId: string,
  data: Partial<DeviceData>,
) {
  const response = await api.put(`/device/${deviceId}`, data);
  return response.data;
}

export async function getPatientInfo() {
  const response = await api.get('/medicalcare/my-info');
  return response.data;
}

export async function updatePatientInfo(data: Partial<PatientInfo>) {
  const response = await api.put(`/medicalcare/my-info`, data);
  return response.data;
}

export async function listFamilies(): Promise<Family[]> {
  const response = await api.get('/family');
  return response.data;
}

export async function createFamily(data: { name: string }): Promise<Family> {
  const response = await api.post('/family', data);
  return response.data;
}

export const useMedicalcareDevices = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const { data, ...rest } = useQuery<unknown, unknown, DeviceData[]>(
    ['medicalcareDevices'],
    () => searchDevices(userId, LinkJapanVendor, MedicalcarePid),
    {
      enabled: !!userId,
    },
  );
  return { medicalcareDevices: data, ...rest };
};

export const useMedicineDeliveryDevices = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const { data, ...rest } = useQuery<unknown, unknown, DeviceData[]>(
    ['medicineDeliveryDevices'],
    () => searchDevices(userId, LinkJapanVendor, MedicineDeliveryPid),
    {
      enabled: !!userId,
    },
  );
  return { medicineDeliveryDevices: data, ...rest };
};

export const useFamilies = () => {
  const { token } = useAuth();
  const { data, ...rest } = useQuery<unknown, unknown, Family[]>(
    ['families'],
    () => listFamilies(),
    {
      enabled: !!token,
    },
  );
  return { families: data, ...rest };
};

export const usePatientInfo = () => {
  const { token } = useAuth();
  const { data, ...rest } = useQuery<unknown, unknown, PatientInfo>(
    ['patientInfo'],
    () => getPatientInfo(),
    {
      enabled: !!token,
    },
  );
  return { patientInfo: data, ...rest };
};

export const useUpdatePatientInfoOnly = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    unknown,
    unknown,
    Partial<PatientInfo>
  >(['updatePatientInfo'], (data) => updatePatientInfo(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['patientInfo']);
    },
  });
  return { updatePatientInfoOnly: mutateAsync, ...rest };
};

export type DeviceConfig = {
  pid: string;
  deviceName: string;
  notificationConfig: {
    appointmentConfirmedTitleTemplate: string;
    appointmentConfirmedBodyTemplate: string;
    appointmentCanceledTitleTemplate: string;
    appointmentCanceledBodyTemplate: string;
    appointmentAmountTitle: string;
    appointmentAmountBody: string;
    appointmentDoctorEnterTitle: string;
    appointmentDoctorEnterBodyTemplate: string;
    appointmentPrescriptionTitle: string;
    appointmentPrescriptionBodyTemplate: string;
    timezone: string;
  };
  queryKey: string;
};

export const deviceConfigByType = (
  type: AppointmentType,
  t: (key: string) => string,
): DeviceConfig => {
  const baseConfig = {
    timezone: 'Asia/Tokyo' as const,
  };

  switch (type) {
    case AppointmentType.medicalcare:
      return {
        pid: MedicalcarePid,
        deviceName: '医療機関連携',
        notificationConfig: {
          ...baseConfig,
          appointmentConfirmedTitleTemplate: t(
            'appointmentNotification.medicalcare.appointmentConfirmedTitleTemplate',
          ),
          appointmentConfirmedBodyTemplate: t(
            'appointmentNotification.medicalcare.appointmentConfirmedBodyTemplate',
          ),
          appointmentCanceledTitleTemplate: t(
            'appointmentNotification.medicalcare.appointmentCanceledTitleTemplate',
          ),
          appointmentCanceledBodyTemplate: t(
            'appointmentNotification.medicalcare.appointmentCanceledBodyTemplate',
          ),
          appointmentAmountTitle: t(
            'appointmentNotification.medicalcare.appointmentAmountTitle',
          ),
          appointmentAmountBody: t(
            'appointmentNotification.medicalcare.appointmentAmountBody',
          ),
          appointmentDoctorEnterTitle: t(
            'appointmentNotification.medicalcare.appointmentDoctorEnterTitle',
          ),
          appointmentDoctorEnterBodyTemplate: t(
            'appointmentNotification.medicalcare.appointmentDoctorEnterBodyTemplate',
          ),
          appointmentPrescriptionTitle: t(
            'appointmentNotification.medicalcare.appointmentPrescriptionTitle',
          ),
          appointmentPrescriptionBodyTemplate: t(
            'appointmentNotification.medicalcare.appointmentPrescriptionBodyTemplate',
          ),
        },
        queryKey: 'medicalcareDevices',
      };
    case AppointmentType.medicine_delivery:
      return {
        pid: MedicineDeliveryPid,
        deviceName: 'お薬サービス',
        notificationConfig: {
          ...baseConfig,
          appointmentConfirmedTitleTemplate: t(
            'appointmentNotification.medicineDelivery.appointmentConfirmedTitleTemplate',
          ),
          appointmentConfirmedBodyTemplate: t(
            'appointmentNotification.medicineDelivery.appointmentConfirmedBodyTemplate',
          ),
          appointmentCanceledTitleTemplate: t(
            'appointmentNotification.medicineDelivery.appointmentCanceledTitleTemplate',
          ),
          appointmentCanceledBodyTemplate: t(
            'appointmentNotification.medicineDelivery.appointmentCanceledBodyTemplate',
          ),
          appointmentAmountTitle: t(
            'appointmentNotification.medicineDelivery.appointmentAmountTitle',
          ),
          appointmentAmountBody: t(
            'appointmentNotification.medicineDelivery.appointmentAmountBody',
          ),
          appointmentDoctorEnterTitle: t(
            'appointmentNotification.medicineDelivery.appointmentDoctorEnterTitle',
          ),
          appointmentDoctorEnterBodyTemplate: t(
            'appointmentNotification.medicineDelivery.appointmentDoctorEnterBodyTemplate',
          ),
          appointmentPrescriptionTitle: t(
            'appointmentNotification.medicineDelivery.appointmentPrescriptionTitle',
          ),
          appointmentPrescriptionBodyTemplate: t(
            'appointmentNotification.medicineDelivery.appointmentPrescriptionBodyTemplate',
          ),
        },
        queryKey: 'medicineDeliveryDevices',
      };
    default:
      throw new Error(`Unsupported appointment type: ${type}`);
  }
};

export const useUpdatePatientInfo = () => {
  // const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { updatePatientInfoOnly } = useUpdatePatientInfoOnly();
  const { patientInfo: currentPatientInfo } = usePatientInfo();
  // const { medicalcareDevices } = useMedicalcareDevices();
  // const { medicineDeliveryDevices } = useMedicineDeliveryDevices();
  // const { families } = useFamilies();

  const { mutateAsync, ...rest } = useMutation<
    unknown,
    unknown,
    Partial<PatientInfo>
  >(
    ['updatePatientInfoAndDevice'],
    async (patientInfo: Partial<PatientInfo>) => {
      // Merge current patient info with input, prioritizing input values
      const finalPatientInfo = {
        ...currentPatientInfo,
        ...patientInfo,
        insuranceImageFront:
          patientInfo.insuranceImageFront ||
          currentPatientInfo?.insuranceImageFront,
        insuranceImageBack:
          patientInfo.insuranceImageBack ||
          currentPatientInfo?.insuranceImageBack,
      };

      // Update patient info
      await updatePatientInfoOnly(finalPatientInfo);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['patientInfo']);
        queryClient.invalidateQueries(['patient-infos']);
      },
    },
  );

  return { updatePatientInfo: mutateAsync, ...rest };
};
